import { ListFilter } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'

import { NotificationTypeFilterItem } from '@/components/notification/notification-type-filter-item'
import { Button, ButtonProps } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'

import { PermissionResource, PermissionAction } from '@/constants/user'
import { useDebounce } from '@/hooks/use-debounce'
import { usePermission } from '@/hooks/use-permission'
import { DEFAULT_FETCH_DEBOUNCE } from '@/lib/constants'
import { NOTIFICATION_TYPE_GROUP } from '@/lib/notification'
import { cn } from '@/lib/utils'
import { NotificationsFilterClient, NotificationTypeGroup } from '@/types/notification'

type NotificationFilterProps = {
  className?: string
  initialFilter?: Partial<NotificationsFilterClient>
  onFilterChange?: (_filter: NotificationsFilterClient) => void
}

const NotificationFilter = ({ className, initialFilter, onFilterChange }: NotificationFilterProps) => {
  const canNotificationIngest = usePermission(PermissionResource.INGESTION, { action: PermissionAction.WRITE })
  const canNotificationScrape = usePermission(PermissionResource.SCRAPER, { action: PermissionAction.WRITE })

  const canNotificationTraining = canNotificationIngest || canNotificationScrape
  const tComponent = useTranslations('Notification.components.notification_filter')

  // local state
  const [isUnreadOnly, setIsUnreadOnly] = useState(initialFilter?.isUnreadOnly ?? false)
  const [groupType, setGroupType] = useState<NotificationTypeGroup[]>(initialFilter?.groupType ?? [])
  const [keyword, setKeyword] = useState(initialFilter?.keyword ?? '')

  const filterState = useMemo<NotificationsFilterClient>(() => {
    return { isUnreadOnly, groupType, keyword: keyword }
  }, [isUnreadOnly, groupType, keyword])

  const debouncedFilter = useDebounce(filterState, DEFAULT_FETCH_DEBOUNCE)

  const notificationTypeFilterItems = useMemo(() => {
    const items = [
      canNotificationTraining
        ? { title: tComponent('filter_type.training'), value: NOTIFICATION_TYPE_GROUP.TRAINING }
        : undefined,
      { title: tComponent('filter_type.history'), value: NOTIFICATION_TYPE_GROUP.CHAT_HISTORY },
      // { title: tComponent('filter_type.organization'), value: NOTIFICATION_TYPE_GROUP.ORGANIZATION },
    ]
    return items.filter(Boolean) as { title: string; value: NotificationTypeGroup }[]
  }, [tComponent, canNotificationTraining])

  const notificationTypeFilterTriggerStyling = useMemo<{
    buttonVariant: ButtonProps['variant']
    logoClassName: string
  }>(() => {
    if (groupType.length > 0) {
      return {
        buttonVariant: 'default',
        logoClassName: 'stroke-base-primary-foreground',
      }
    }
    return {
      buttonVariant: 'outline',
      logoClassName: '',
    }
  }, [groupType])

  // notify parent of changes
  useEffect(() => {
    onFilterChange?.(debouncedFilter)
  }, [debouncedFilter, onFilterChange])

  return (
    <div className={className}>
      <div className="mb-3.5 flex items-center justify-between  gap-2 ">
        <span className="inline-block truncate font-medium">{tComponent('title')}</span>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <span className="text-sm font-medium">{tComponent('labels.unread')}</span>
          <Switch checked={isUnreadOnly} onCheckedChange={setIsUnreadOnly} />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <Input
          name="searchNotification"
          placeholder={tComponent('placeholders.search')}
          className="grow-1 px-3 py-2"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant={notificationTypeFilterTriggerStyling.buttonVariant} size="icon" className="shrink-0">
              <ListFilter className={cn(notificationTypeFilterTriggerStyling.logoClassName, 'size-4')} />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="border-base w-fit min-w-40 overflow-hidden rounded-xl p-1 shadow-xl"
            align="end"
            sideOffset={10}
          >
            {notificationTypeFilterItems.map((item) => (
              <NotificationTypeFilterItem
                key={item.title}
                title={item.title}
                value={groupType.includes(item.value)}
                onCheckedChange={(value) => {
                  if (value) {
                    setGroupType([...groupType, item.value])
                  } else {
                    setGroupType(groupType.filter((typeItem) => typeItem !== item.value))
                  }
                }}
              />
            ))}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
NotificationFilter.displayName = 'NotificationFilter'

export { NotificationFilter }
